import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getUser, getUserCreditHistory, dashboardUserAudios, getAdminNotifications, GET_ALL_VOCALS } from '../../graphql/queries';
import { createOneCredit } from '../../graphql/mutation';
import { Card, Tag, Spin, Alert, Table, Image, Tabs, Tooltip, Space, Button, message, Row, Col, Modal, InputNumber, Form } from 'antd';
import { CustomerServiceOutlined, CopyOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import './UserDetail.css';

const { TabPane } = Tabs;

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notificationPage, setNotificationPage] = useState(0);
  const [notificationPageSize, setNotificationPageSize] = useState(10);
  const [creditModalVisible, setCreditModalVisible] = useState(false);
  const [creditModalType, setCreditModalType] = useState('add'); // 'add' or 'remove'
  const [creditForm] = Form.useForm();

  const { loading: userLoading, error: userError, data: userData, refetch: refetchUser } = useQuery(getUser, {
    variables: { userId: id },
  });
  const { data: creditData, refetch: refetchCreditHistory } = useQuery(getUserCreditHistory, {
    variables: { userId: id }
  });
  const { data: audioData } = useQuery(dashboardUserAudios, {
    variables: { rcUserId: userData?.getUser?.rcUserId },
    skip: !userData?.getUser?.rcUserId
  });
  const { data: notificationData, loading: notificationLoading } = useQuery(getAdminNotifications, {
    variables: {
      userId: id,
      page: notificationPage + 1,
      pageSize: notificationPageSize
    }
  });
  const { data: vocalData, loading: vocalLoading } = useQuery(GET_ALL_VOCALS, {
    variables: { userId: id },
    skip: !id
  });

  const [addCredit, { loading: creditLoading }] = useMutation(createOneCredit, {
    onCompleted: () => {
      message.success(creditModalType === 'add' ? 'Kredi başarıyla eklendi' : 'Kredi başarıyla çıkarıldı');
      setCreditModalVisible(false);
      creditForm.resetFields();
      refetchUser();
      refetchCreditHistory();
    },
    onError: (error) => {
      message.error(`Hata: ${error.message}`);
    }
  });

  const handleCreditSubmit = async () => {
    try {
      const values = await creditForm.validateFields();
      const amount = creditModalType === 'add' ? values.amount : -values.amount;
      await addCredit({
        variables: {
          userId: id,
          amount: amount,
          date: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const openCreditModal = (type) => {
    setCreditModalType(type);
    setCreditModalVisible(true);
    creditForm.resetFields();
  };

  if (userLoading) return <Spin size="large" className="user-detail-loading" />;
  if (userError) return <Alert type="error" message={`Error: ${userError.message}`} />;

  const user = userData.getUser;
  const creditHistory = creditData?.getUserCreditHistory || [];
  const audios = audioData?.getAllAudios?.audios || [];
  const notifications = notificationData?.getAdminNotifications?.notifications || [];
  const vocals = vocalData?.getAllVocals?.vocals || [];

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getNotificationTypeColor = (type) => {
    const colors = {
      LIKE: 'blue',
      MILESTONE_10: 'green',
      MILESTONE_25: 'green',
      MILESTONE_50: 'gold',
      MILESTONE_100: 'red',
      FEED_CONFIRMED: 'cyan',
      VIDEO_COMPLETED: 'green',
      VIDEO_FAILED: 'red',
      CREDIT_RECEIVED: 'purple',
      DAILY_STREAK: 'orange',
      PROMPT_COPY: 'magenta',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  const notificationColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color={getNotificationTypeColor(type)}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (title) => (
        <Tooltip placement="topLeft" title={title}>
          {title}
        </Tooltip>
      ),
    },
    {
      title: 'Body',
      dataIndex: 'body',
      key: 'body',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (body) => (
        <Tooltip placement="topLeft" title={body}>
          {body}
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 100,
      render: (isRead) => (
        <Tag color={isRead ? 'default' : 'blue'}>
          {isRead ? 'Read' : 'Unread'}
        </Tag>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 180,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    },
    {
      title: 'Read At',
      dataIndex: 'readAt',
      key: 'readAt',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }) : '-',
    },
  ];

  const creditColumns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      width: 250,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
    },
    {
      title: 'Credit ID',
      dataIndex: 'creditId',
      key: 'creditId',
      width: 250,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const audioColumns = [
    {
      title: 'Cover',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      render: (url) => (
        <Image
          src={url}
          alt="Cover"
          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
          preview={{
            maskClassName: 'customize-mask',
            mask: <div>View</div>
          }}
        />
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (text) => text || '-',
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const colors = {
          SUCCEEDED: 'green',
          FAILED: 'red',
          RUNNING: 'blue',
          PENDING: 'orange',
          IN_QUE: 'cyan',
          THROTTLED: 'purple',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Source',
      dataIndex: 'generationSource',
      key: 'generationSource',
      width: 100,
      render: (source) => (
        <Tag color={source === 'FAL_AI' ? 'blue' : 'green'}>{source}</Tag>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => duration ? `${duration}s` : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const vocalColumns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url) => url ? <Image width={50} height={50} src={url} style={{ borderRadius: 4 }} /> : '-',
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 100,
      render: (model) => model || 'V4_5PLUS',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const colors = {
          SUCCEEDED: 'green',
          FAILED: 'red',
          RUNNING: 'blue',
          PENDING: 'orange',
          IN_QUE: 'cyan',
          THROTTLED: 'purple',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Progress',
      dataIndex: 'progressRatio',
      key: 'progressRatio',
      width: 100,
      render: (progress) => progress !== null ? `${progress}%` : '-',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => duration ? `${duration}s` : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const handleSupportClick = () => {
    if (user?.rcUserId) {
      navigate(`/support?rcUserId=${user.rcUserId}`);
    } else {
      message.warning('RevenueCat User ID not available');
    }
  };

  return (
    <div className="user-detail-container">
      <Card
        title={
          <Space>
            <span>User Details</span>
            <Button
              type="primary"
              icon={<CustomerServiceOutlined />}
              onClick={handleSupportClick}
              disabled={!user?.rcUserId}
            >
              Go to Support
            </Button>
          </Space>
        }
      >
        <div className="user-info-section">
          <Row gutter={[24, 24]}>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">RevenueCat ID</div>
                <div className="detail-value with-copy">
                  <span className="text-value">{user.rcUserId || '-'}</span>
                  {user.rcUserId && (
                    <Tooltip title="Copy ID">
                      <CopyOutlined
                        className="copy-icon"
                        onClick={() => {
                          navigator.clipboard.writeText(user.rcUserId);
                          message.success('RevenueCat ID copied to clipboard');
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">User ID</div>
                <div className="detail-value with-copy">
                  <span className="text-value">{user.id}</span>
                  <Tooltip title="Copy ID">
                    <CopyOutlined
                      className="copy-icon"
                      onClick={() => {
                        navigator.clipboard.writeText(user.id);
                        message.success('User ID copied to clipboard');
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Store</div>
                <div className="detail-value">
                  <span className="text-value">{user.store || '-'}</span>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">
                  FCM ID
                  {user.fcmId && (
                    <Tooltip title="Copy FCM ID">
                      <Tag
                        color="blue"
                        icon={<CopyOutlined />}
                        style={{ 
                          marginLeft: '8px', 
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(user.fcmId);
                          message.success('FCM ID copied to clipboard');
                        }}
                      >
                        Copy
                      </Tag>
                    </Tooltip>
                  )}
                </div>
                <div className="detail-value">
                  <Tooltip title={user.fcmId}>
                    <span className="text-value">
                      {user.fcmId ? truncateText(user.fcmId, 20) : '-'}
                    </span>
                  </Tooltip>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Role</div>
                <div className="detail-value">
                  <Tag color={user.role === 'ADMIN' ? 'green' : 'blue'}>
                    {user.role}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Premium Status</div>
                <div className="detail-value">
                  <Tag color={user.isPremium ? 'gold' : 'default'}>
                    {user.isPremium ? 'Premium' : 'Regular'}
                  </Tag>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Premium Expiry</div>
                <div className="detail-value">
                  <span className="text-value">
                    {user.premiumExpiryDate ? new Date(user.premiumExpiryDate).toLocaleString('tr-TR', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </span>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">First Seen Location</div>
                <div className="detail-value">
                  <span className="text-value">{user.firstSeenLocation || '-'}</span>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Last Seen Location</div>
                <div className="detail-value">
                  <span className="text-value">{user.lastSeenLocation || '-'}</span>
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Total Credit</div>
                <div className="detail-value">
                  <Space>
                    <span className="text-value" style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {user.totalCredit?.total || 0}
                    </span>
                    <Tooltip title="Kredi Ekle">
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => openCreditModal('add')}
                        style={{ padding: '0 8px', height: '24px', fontSize: '12px' }}
                      />
                    </Tooltip>
                    <Tooltip title="Kredi Çıkar">
                      <Button
                        danger
                        size="small"
                        icon={<MinusOutlined />}
                        onClick={() => openCreditModal('remove')}
                        style={{ padding: '0 8px', height: '24px', fontSize: '12px' }}
                      />
                    </Tooltip>
                  </Space>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Daily Credit</div>
                <div className="detail-value">
                  <span className="text-value">{user.totalCredit?.dailyCredit || 0}</span>
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="detail-item">
                <div className="detail-label">Used Credit</div>
                <div className="detail-value">
                  <span className="text-value">{user.usedCreditCount || 0}</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Tabs defaultActiveKey="1" className="detail-tabs">
          <TabPane tab="Credit History" key="1">
            <Table
              columns={creditColumns}
              dataSource={creditHistory}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                defaultPageSize: 5
              }}
              scroll={{ x: 'max-content' }}
            />
          </TabPane>
          
          <TabPane tab="Audios" key="2">
            <Table
              columns={audioColumns}
              dataSource={audios}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                defaultPageSize: 5
              }}
              scroll={{ x: 'max-content' }}
              onRow={(record) => ({
                onClick: () => navigate(`/audio/${record.id}`),
                style: { cursor: 'pointer' }
              })}
            />
          </TabPane>

          <TabPane tab="Vocals" key="3">
            <Table
              columns={vocalColumns}
              dataSource={vocals}
              rowKey="id"
              loading={vocalLoading}
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                defaultPageSize: 5
              }}
              scroll={{ x: 'max-content' }}
              onRow={(record) => ({
                onClick: () => navigate(`/vocals/${record.id}`),
                style: { cursor: 'pointer' }
              })}
            />
          </TabPane>

          <TabPane tab="Notifications" key="4">
            <Table
              columns={notificationColumns}
              dataSource={notifications}
              rowKey="id"
              loading={notificationLoading}
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                pageSize: notificationPageSize,
                current: notificationPage + 1,
                onChange: (page, pageSize) => {
                  setNotificationPage(page - 1);
                  setNotificationPageSize(pageSize);
                },
              }}
              scroll={{ x: 'max-content' }}
              rowClassName={(record) => !record.isRead ? 'unread-notification-row' : ''}
            />
          </TabPane>

        </Tabs>
      </Card>

      <Modal
        title={creditModalType === 'add' ? 'Kredi Ekle' : 'Kredi Çıkar'}
        open={creditModalVisible}
        onOk={handleCreditSubmit}
        onCancel={() => {
          setCreditModalVisible(false);
          creditForm.resetFields();
        }}
        confirmLoading={creditLoading}
        okText={creditModalType === 'add' ? 'Ekle' : 'Çıkar'}
        cancelText="İptal"
      >
        <Form form={creditForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Miktar"
            rules={[
              { required: true, message: 'Lütfen miktar girin' },
              { type: 'number', min: 1, message: 'Miktar en az 1 olmalıdır' }
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder={creditModalType === 'add' ? 'Eklenecek kredi miktarı' : 'Çıkarılacak kredi miktarı'}
            />
          </Form.Item>
        </Form>
        {creditModalType === 'remove' && (
          <Alert
            message="Dikkat"
            description="Bu işlem kullanıcının kredisini düşürecektir."
            type="warning"
            showIcon
          />
        )}
      </Modal>
    </div>
  );
};

export default UserDetail; 